import * as React from 'react';
import { AnswersPage, Survey, SurveySegment, getSegmentListKey2, segmentListIsInLists, surveyHelper, surveySegmentListCompare } from '../../../../../../amorg-common';
import { hasUserPermissions } from '../../../../../../api/serverApiHelpers';
import { deleteAllSurveyAnswers, getSurveyAnswersPage, getSurveySegmentReport } from '../../../../../../api/surveyClientApi';
import { Button } from '../../../../../../designSystem/button/Button';
import { ToolBarButton, ToolBarContainer } from '../../../../../../designSystem/compositeComponents/ButtonToolBar';
import { CardPager } from '../../../../../../designSystem/compositeComponents/CardPager';
import { CloseModalButton } from '../../../../../../designSystem/compositeComponents/CloseModalButton';
import { DeleteConfirmationModal } from '../../../../../../designSystem/compositeComponents/DeleteConfirmationModal';
import { Flex } from '../../../../../../designSystem/compositeComponents/FlexHelpers';
import { RadioButtonList } from '../../../../../../designSystem/inputs/RadioButton';
import { TextInput } from '../../../../../../designSystem/inputs/TextInput';
import { LoadingPageOverlay } from '../../../../../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../../../../../designSystem/MessageCard';
import { Modal } from '../../../../../../designSystem/Modal';
import { Table, TableHeaderWithSort } from '../../../../../../designSystem/Table';
import { timeToClose, timeToOpen, toDateTimeString } from '../../../../../../utils/date';
import { SegmentListChipList } from '../_components/SegmentListChipList';
import { SegmentListView, SegmentListViewData } from '../_components/SegmentListView';
import { WorkplaceAssessmentComponentProps } from '../_components/WorkplaceAssessmentComponentProps';
import { GenerateAnswersModal } from '../survey/_components/GenerateAnswersModal';
import { AnswerGraphPanel } from './_components/AnswerGraphPanel';
import { SendInvitationsPanel } from './_components/SendInvitationsPanel';
import { getSurveyStatusLabel } from '../survey/_components/SurveyStatusPanel';
import { SingleColumnLayout } from '../../../../../_layouts/SingleColumnLayout';
import { PageHeader } from '../../../../../../designSystem/PageHeader';
import { SimpleStatCard, StatCardRow, StatOutOfStatCard } from '../../../../../../designSystem/StatCard';
import {  PageSection } from '../../../../../../designSystem/PageSection';

const PAGE_SIZE = 25;

type AnswerCompletion = 'all' | 'complete' | 'incomplete';
type SortBy = 'email' | 'started' | 'finished';

type State = {
    loading: boolean,

    searchTerm: string,
    segmentFilter: SurveySegment[][],
    completion: AnswerCompletion,

    page: number,
    sortBy: SortBy,
    sortAscending: boolean,

    answerPage?: AnswersPage,

    lastAnswerUpdate: Date;
};

export default function AnswersPanel (props: WorkplaceAssessmentComponentProps & { survey: Survey }): JSX.Element
{

    const [state, setState] = React.useState<State>({
        loading: true,

        searchTerm: '',
        segmentFilter: [],
        completion: 'all',

        page: 1,
        sortBy: 'finished',
        sortAscending: true,

        answerPage: undefined,
        lastAnswerUpdate: new Date()
    });

    const [showGenerateAnswersModal, setShowGenerateAnswersModal] = React.useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
    const [showFilter, setShowFilter] = React.useState(false);
    const [showInvitationsModal, setShowInvitationsModal] = React.useState(false);

    React.useEffect(function ()
    {
        reloadAnswers({ ...state, page: 1 });
    }, []);

    async function reloadAnswers (args: State)
    {
        setState({ ...state, loading: true });
        setState({
            ...state,
            ...args,
            answerPage: await getSurveyAnswersPage(props.survey.id, args.searchTerm, args.segmentFilter, args.completion, args.page, PAGE_SIZE, args.sortBy, args.sortAscending),
            loading: false
        });
    }

    async function handlePageChange (newPage: number) 
    {
        await reloadAnswers({ ...state, page: newPage });
    }

    async function handleDeleteAllSurveyAnswers ()
    {
        await deleteAllSurveyAnswers(props.survey.id);
        setShowDeleteConfirmation(false);
        await reloadAnswers({ ...state, page: 1, lastAnswerUpdate: new Date() });
        props.onSurveyUpdated();
    }

    async function handleAnswersChanged ()
    {
        await reloadAnswers({ ...state, page: 1, lastAnswerUpdate: new Date() });
        props.onSurveyUpdated();
    }

    async function handleClickSort (sortBy: SortBy)
    {
        setState({ ...state, loading: true });

        const sortAscending = sortBy === state.sortBy
            ? !state.sortAscending
            : true;

        reloadAnswers(
            {
                ...state,
                sortBy,
                sortAscending
            }
        );
    }    

    const surveyStatus = surveyHelper.getSurveyStatus(props.survey);

    const canSubmitAnswer = surveyStatus.canSubmitAnswer;

    const canGenerateTestAnswers =
        props.activeUser?.permissionGroupId === 'superadmin'
        && canSubmitAnswer
        && props.survey.answerCount === 0;

    const canSendInvitations =
        hasUserPermissions(props.activeUser, ['SendSurveyRespondentEmails'])
        && canSubmitAnswer;

    const canDelete =
        hasUserPermissions(props.activeUser, ['DeleteAllSurveyAnswers'])
        && props.survey.answerCount > 0;

    const showPager = state.answerPage && state.answerPage.pageCount > 1;

    const now = new Date();
    const remainingTime = now < props.survey.end
        ? timeToClose(props.survey.end, now)
        : timeToOpen(props.survey.start, now);

    const safeCompletionPct = props.survey.answerCount === 0 ? 0 : 
        (props.survey.finishedAnswerCount / props.survey.answerCount) * 100;

    return (
        <>
            <PageHeader
                headerText="APV - besvarelser"
                subHeader={getSurveyStatusLabel(props.survey)}
                buttons={
                    <ToolBarContainer>

                        {props.activeUser.permissionGroupId === 'superadmin' &&
                            <ToolBarButton
                                icon="interface_actions_send"
                                tooltip={canGenerateTestAnswers
                                    ? "Opret testbesvarelser"
                                    : "Du skal være superadministrator, og spørgeskemaet skal være åbent og ikke have besvarelser for at kunne oprette testbesvarelser"
                                }
                                onClick={!canGenerateTestAnswers ? undefined : () => setShowGenerateAnswersModal(true)}
                                variant="text"
                                disabled={!canGenerateTestAnswers}
                            />
                        }

                        <ToolBarButton
                            icon="interface_actions_mail"
                            tooltip={canSendInvitations
                                ? "Send e-mail invitationer"
                                : "Spørgeskemaet skal være åbent for besvarelser og du skal være administrator for at sende e-mail invitationer"
                            }
                            onClick={!canSendInvitations ? undefined : () => setShowInvitationsModal(true)}
                            variant="text"
                            disabled={!canSendInvitations}
                        />

                        <ToolBarButton
                            icon="interface_actions_delete"
                            tooltip="Slet alle besvarelser"
                            onClick={!canDelete ? undefined : () => setShowDeleteConfirmation(true)}
                            variant="destructive-text"
                            disabled={!canDelete}
                        />

                    </ToolBarContainer>
                }
            />

            <SingleColumnLayout>
                {props.survey.answerCount === 0 &&
                    <MessageCard variant='info'>
                        Ingen besvarelser er oprettet
                    </MessageCard>
                }

            <StatCardRow>
                <StatOutOfStatCard
                    header='Fuldførte besvarelser'
                    stat={props.survey.finishedAnswerCount} 
                    outOf={props.survey.answerCount}
                    subText={`${Math.round(safeCompletionPct)}% gennemført`}
                />
                <SimpleStatCard
                    header='Aktive segmeter'
                    stat={props.surveyReport?.selectionReports.length.toString() || '0'} 
                    subText="Segmenter med oprettede besvarelser"
                />
                <SimpleStatCard
                    header='Tid tilbage'
                    stat={remainingTime} 
                    subText={`${toDateTimeString(props.survey.start)} - ${toDateTimeString(props.survey.end)}`}
                    fontSizePx={24}
                />

            </StatCardRow>

            {props.survey.answerCount > 0 &&
                <AnswerGraphPanel survey={props.survey} triggerTimestamp={state.lastAnswerUpdate} />
            }

            <Flex flexDirection='column' gap={16}>
                <PageSection
                    header={{ 
                        title: 'Alle besvarelser',
                        customButtonContent:
                        <Flex flexDirection='row' justifyContent='flex-start' alignItems='center' gap={10}>
                                <Button variant="text" icon='interface_actions_filter' onClick={() => setShowFilter(true)} />
                                <div style={{ width: '300px' }}>
                                    <TextInput trailingIcon='interface_actions_search'
                                        value={state.searchTerm}
                                        onChange={(value) => setState({ ...state, searchTerm: value })}
                                        onKeyDown={(e) =>
                                            {
                                                if (e.key === 'Enter')
                                                    reloadAnswers({ ...state });
                                            }} />
                                </div>
                            </Flex>
                    }}
                    headerStyleVariant='card'
                    contentStyleVariant='card'
                    contentPadding="0px">

                    <Table style={{width:'100%'}} noOuterBorder>
                        <thead>
                            <tr>
                                <TableHeaderWithSort text='Email'
                                    isActiveSort={state.sortBy === 'email'} ascending={state.sortAscending} onClick={() => handleClickSort('email')}
                                />
                                <th>Segmenter</th>
                                <TableHeaderWithSort text='Besvarelse oprettet'
                                    width='200px'
                                    isActiveSort={state.sortBy === 'started'} ascending={state.sortAscending} onClick={() => handleClickSort('started')}
                                />
                                <TableHeaderWithSort text='Besvarelse gennemført'
                                    width='200px'
                                    isActiveSort={state.sortBy === 'finished'} ascending={state.sortAscending} onClick={() => handleClickSort('finished')}
                                />
                            </tr>
                        </thead>
                        <tbody>
                            {state.answerPage?.answers.length === 0 &&
                                <tr>
                                    <td colSpan={4}>
                                        <i>Ingen besvarelser</i>
                                    </td>
                                </tr>
                            }
                            {(state.answerPage?.answers.length || 0)  > 0 && state.answerPage?.answers.map(a =>
                            {
                                return (
                                    <tr key={a.id}>
                                        <td>
                                            {a.email}
                                        </td>
                                        <td>
                                            <SegmentListChipList segments={a.segments} identifier={a.id} />
                                        </td>
                                        <td>
                                            {a.started ? toDateTimeString(a.started) : ''}
                                        </td>
                                        <td>
                                            {a.finished ? toDateTimeString(a.finished) : ''}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>

                    {showPager &&
                        <CardPager
                            items={PAGE_SIZE * (state.answerPage?.pageCount || 0)}
                            itemsPerPage={PAGE_SIZE}
                            currentPage={state.page}
                            onPageChange={handlePageChange} />
                    }
                </PageSection>
            </Flex>

            <LoadingPageOverlay loading={state.loading} />

            <FilterModal
                show={showFilter}
                survey={props.survey}
                filter={state.segmentFilter}
                completion={state.completion}
                onChange={(completion, segmentFilter) => reloadAnswers({ ...state, completion, segmentFilter })}
                onClose={() => setShowFilter(false)}
            />

            <GenerateAnswersModal
                show={showGenerateAnswersModal}
                survey={props.survey}
                onUpdated={handleAnswersChanged}
                disabled={!(surveyStatus && surveyStatus.canSubmitAnswer)}
                onClose={() => setShowGenerateAnswersModal(false)}
            />

            <DeleteConfirmationModal
                showModal={showDeleteConfirmation}
                buttonText="Slet alle besvarelser"
                confirmationHeading="Slet besvarelser?"
                confirmationText="Bemærk at denne handling vil slette alle besvarelser og IKKE kan fortrydes"
                requireKey={true}
                onConfirmDelete={handleDeleteAllSurveyAnswers}
                onCancel={() => setShowDeleteConfirmation(false)} />

            <SendInvitationsModal
                show={showInvitationsModal}
                survey={props.survey}
                onClose={() => setShowInvitationsModal(false)}
                onSent={handleAnswersChanged}
            />
            </SingleColumnLayout>
        </>
    );
}



type FilterModalProps = {
    survey: Survey,
    show: boolean,
    filter: SurveySegment[][],
    completion: AnswerCompletion,
    onChange: (completion: AnswerCompletion, segmentFilter: SurveySegment[][]) => void,
    onClose: () => void;
};
function FilterModal (props: FilterModalProps): JSX.Element
{
    const [segmentList, setSegmentList] = React.useState<SegmentListViewData[]>([]);

    React.useEffect(() =>
    {
        async function handle()
        {
            let data = segmentList;
            if (data.length === 0)
            {
                const newReport = await getSurveySegmentReport(props.survey.id);
                data = newReport.map(sr => ({
                    segments: sr.segments,
                    answerCount: sr.answerCount,
                    identifier: getSegmentListKey2(sr.segments),
                    selected: false
                })).filter(sl => sl.answerCount > 0)
                data.sort((a, b) => surveySegmentListCompare(props.survey.segmentations, a.segments, b.segments));
            }

            setSegmentList(data);
        }
        handle();
    }, []);

    React.useEffect(() =>
    {
        if (props.show && segmentList.length > 0)
        {
            setSegmentList(
                segmentList.map(sl => ({
                    ...sl,
                    selected: segmentListIsInLists(props.filter, sl.segments)
                }))
            );
        }

    }, [props.filter]);

    function handleSelectionChanged (identifier: string, selected: boolean)
    {
        const match = segmentList.find(sl => getSegmentListKey2(sl.segments) === identifier);
        if (match)
        {
            if(selected)
            {
                props.onChange(
                    props.completion,
                    [
                        ...segmentList.filter(ld => ld.selected).map(ld => ld.segments),
                        match.segments
                    ]
                );
            }
            else {
                props.onChange(
                    props.completion,
                    segmentList.filter(ld => ld.selected && getSegmentListKey2(ld.segments) !== identifier).map(ld => ld.segments)
                );
            }
        }
    }

    return (

        <Modal
            title={"Filtrér besvarelser"}
            content={
                <>
                    <RadioButtonList gap={16}
                        groupName="completion"
                        flexDirection='row'
                        options={[
                            { text: 'Alle', value: 'all' }, { text: 'Gennemførte', value: 'complete' }, { text: 'Ikke-gennemførte', value: 'incomplete' }
                        ]}
                        selectedValue={props.completion}
                        onChange={(value) => props.onChange(value as AnswerCompletion, props.filter)} />

                    {segmentList && segmentList.length > 0 &&
                        <SegmentListView
                            view='table'
                            segmentLists={segmentList}
                            displayAnswerCount
                            onToggleSelect={handleSelectionChanged}
                        />
                    }

                    <CloseModalButton
                        onClose={props.onClose} />
                </>
            }
            showModal={props.show}
            onCancel={props.onClose} />


    );
}

type SendInvitationsModalProps = {
    show: boolean,
    survey: Survey,
    onClose: () => void,
    onSent: () => void;
};
function SendInvitationsModal (props: SendInvitationsModalProps): JSX.Element
{
    return (
        <>
            {props.show &&
                <Modal
                    title={"Send e-mail invitationer"}
                    content={
                        <SendInvitationsPanel survey={props.survey} onClose={props.onClose} onSent={props.onSent} />
                    }
                    showModal={props.show}
                    onCancel={props.onClose} />

            }
        </>
    );
}



